import { useEffect, useRef } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/30";

export function Field({ label, value, onChange, type = "text", rows = 3, wide, testid }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      {type === "lines" || type === "textarea" ? (
        <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} mt-1 resize-y`} data-testid={testid} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} mt-1`} data-testid={testid} />
      )}
    </label>
  );
}

export function Collapsible({ id, title, count, hint, open, onToggle, action, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl" data-testid={`resume-section-${id}`} data-open={open ? "true" : "false"}>
      <div className="flex items-center gap-3 px-5 py-3.5">
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex-1 min-w-0 flex items-center gap-3 text-left" data-testid={`resume-section-toggle-${id}`}>
          <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
          <span className="min-w-0">
            <h3 className="font-heading text-lg text-slate-900 leading-tight">{title} {count != null && <span className="text-xs text-slate-400 font-sans">{count}</span>}</h3>
            {hint && open && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
          </span>
        </button>
        {action}
      </div>
      {open && <div className="px-5 pb-5">{children}</div>}
    </section>
  );
}

export function ListEditor({ id, title, hint, items, fields, onChange, addLabel, blank, open, onToggle }) {
  const update = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const a = [...items]; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  const justAdded = useRef(false);
  const firstItem = useRef(null);
  // New entries go to the TOP of the section and get focus, so nobody has to scroll down hunting for the empty box.
  const add = () => { justAdded.current = true; onChange([{ ...blank }, ...items]); if (!open) onToggle(); };
  useEffect(() => {
    if (!justAdded.current || !firstItem.current) return;
    justAdded.current = false;
    firstItem.current.scrollIntoView({ behavior: "smooth", block: "center" });
    firstItem.current.querySelector("input, textarea")?.focus({ preventScroll: true });
  }, [items.length, open]);
  return (
    <Collapsible id={id} title={title} count={items.length} hint={hint} open={open} onToggle={onToggle}
      action={<button type="button" onClick={add} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline shrink-0" data-testid={`resume-add-${id}`}><Plus className="w-3.5 h-3.5" /> {addLabel}</button>}>
      {items.length === 0 && <p className="text-sm text-slate-400 italic">Nothing here yet.</p>}
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} ref={i === 0 ? firstItem : null} className="rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid={`resume-${id}-item-${i}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fields.map((f) => (
                <Field key={f.k} label={f.l} wide={f.w} type={f.type} rows={f.type === "lines" ? 4 : 2}
                  value={f.type === "lines" ? (it[f.k] || []).join("\n") : it[f.k] || ""}
                  onChange={(v) => update(i, f.k, f.type === "lines" ? v.split("\n") : v)}
                  testid={`resume-${id}-${i}-${f.k}`} />
              ))}
            </div>
            <div className="flex items-center justify-end gap-1 mt-2">
              <button type="button" onClick={() => move(i, -1)} className="p-1.5 text-slate-400 hover:text-slate-700" title="Move up" data-testid={`resume-${id}-${i}-up`}><ChevronUp className="w-4 h-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1.5 text-slate-400 hover:text-slate-700" title="Move down" data-testid={`resume-${id}-${i}-down`}><ChevronDown className="w-4 h-4" /></button>
              <button type="button" onClick={() => remove(i)} className="p-1.5 text-slate-400 hover:text-red-600" title="Remove" data-testid={`resume-${id}-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </Collapsible>
  );
}
